import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { useUser } from '@/contexts/user/User.context'
import { Country } from '@/data/countriesClusters'
import { getFromLocalStorage, setToLocalStorage } from '@/helpers/localstorage'

import Alert from '../ui/Alert/Alert'
import Dialog from '../ui/Dialog/Dialog'
import Link from '../ui/Link/Link'
import { notificationDanger } from '../ui/Notification/Notification'

import { Dac7Form } from './Dac7Form'

import './Dac7Dialog.scss'

const BLOCK_SELLER_STUDIO_AFTER = dayjs('2026-01-13 06:00:00')
const LIMIT_DATE_FOR_SUBMISSION = dayjs('2026-01-09')
const DAC7_MODAL_LAST_CLOSED = 'dac7_modal_last_closed'

export const Dac7Dialog = () => {
  const { t } = useTranslation()
  const { user } = useUser()
  const { sellerConfig, username, firstName } = user || {}
  const { isEligibleForDac7, dac7ValidatedAt, country } = sellerConfig || {}

  const [isOpen, setIsOpen] = useState<boolean>(false)

  const isAfterLokedDate = dayjs().isAfter(BLOCK_SELLER_STUDIO_AFTER)

  useEffect(() => {
    const isEligible = Boolean(isEligibleForDac7)
    const hasAlreadyValidated = Boolean(dac7ValidatedAt)
    const lastClosed = getFromLocalStorage(DAC7_MODAL_LAST_CLOSED)
    const lastClosedDate = lastClosed ? dayjs(lastClosed) : null
    const isClosedBeforeToday = lastClosedDate ? lastClosedDate.isBefore(dayjs(), 'day') : false

    const shouldBeOpen =
      isEligible &&
      !hasAlreadyValidated &&
      (!lastClosed || isClosedBeforeToday || LIMIT_DATE_FOR_SUBMISSION.isBefore(dayjs()))

    if (shouldBeOpen !== isOpen) {
      setIsOpen(shouldBeOpen)
    }
  }, [isEligibleForDac7, dac7ValidatedAt])

  const handleClose = () => {
    if (isAfterLokedDate && isOpen) {
      notificationDanger(t('cantCloseDac7Form'))
      return
    }
    setIsOpen(false)
    setToLocalStorage(DAC7_MODAL_LAST_CLOSED, dayjs().format())
  }

  if (!user) return null

  return (
    <Dialog className="dac7-dialog" isOpen={isOpen} title={t('dac7ModalTitle')} onClose={handleClose}>
      <div className="dac7-dialog-header">
        {isAfterLokedDate && (
          <Alert className="dac7-dialog-alert" emphasis="high" type="warning">
            {t('dac7AlertLocked')}
          </Alert>
        )}
        <p>
          <Trans i18nKey="dac7ModalContent" values={{ username: username ?? firstName }} />
        </p>
        {country?.iso2Code === Country.NL ? (
          <Link href={t('dac7LinkDocNL')} rel="noreferrer" target="_blank">
            {t('commonTextLearnMore')}
          </Link>
        ) : (
          <Link href={t('dac7LinkDoc')} rel="noreferrer" target="_blank">
            {t('commonTextLearnMore')}
          </Link>
        )}
      </div>
      <div className="dac7-modal-content">
        <Dac7Form />
      </div>
    </Dialog>
  )
}
